<template>
  <q-slide-item
    data-test-id="kitchen-order-slide-item"
    class="no-wrap"
    :style="orderCardStyles"
    left-color="negative"
    right-color="positive"
    @left="({ reset }) => handleSwipeLeft(reset)"
    @right="({ reset }) => handleSwipeRight(reset)"
    @click="promptOrderActions()"
  >
    <template
      v-if="canSwipeLeft"
      #left
    >
      <q-icon
        name="backspace"
        left
      />
    </template>
    <template
      v-if="canSwipeRight"
      #right
    >
      <q-icon
        name="checkmark"
        right
      />
    </template>
    <q-item
      :class="$q.screen.lt.md ? 'column' : 'row q-py-xs items-start'"
      style="min-height: 40px;"
    >
      <div
        class="column justify-start items-start text-subtitle2"
        :class="{ 'q-pr-md': $q.screen.gt.md }"
        style="flex-grow: 1;"
      >
        <div
          class="full-width relative-position"
          :style="{
            'font-size': !!orderOriginLabel ? '1.25rem' : '1.5rem',
            'line-height': !!orderOriginLabel ? '1.5rem' : '2rem'
          }"
        >
          <span style="visibility: hidden;">PLACEHOLDER</span>
          <div class="absolute-full ellipsis">
            {{ orderDisplayName }}
          </div>
        </div>
        <div
          v-if="!!orderOriginLabel"
          class="full-width relative-position text-caption"
          style="line-height: 1rem;"
        >
          <span style="visibility: hidden;">PLACEHOLDER</span>
          <div
            class="absolute-full ellipsis"
          >
            {{ orderOriginLabel }}
          </div>
        </div>
      </div>
      <div
        v-if="!!orderDeliveryTypeLabel || !!orderDeliveryLocationsLabel"
        class="column justify-start items-end text-subtitle2 text-right"
        :style="{ 'line-height': !!orderOriginLabel ? '1.2rem' : '1rem' }"
      >
        <div
          v-if="!!orderDeliveryTypeLabel"
          class="ellipsis"
        >
          {{ orderDeliveryTypeLabel }}
        </div>
        <div
          v-if="!!orderDeliveryLocationsLabel"
          class="ellipsis"
        >
          {{ orderDeliveryLocationsLabel }}
        </div>
      </div>
    </q-item>
    <q-separator color="grey-7" />
    <q-item
      class="text-subtitle2 items-center justify-between q-py-xs q-ma-none"
      style="min-height: 32px; max-height: 32px;"
    >
      <div>{{ dueTime }}</div>
      <div v-if="tab !== STAGES.COMPLETE">
        {{ relativeTime }}
      </div>
      <div v-else>
        {{ deliveredTime }}
      </div>
    </q-item>
  </q-slide-item>
</template>

<script>
import { isElectronMode } from 'src/config'
import { DELIVERY_TYPE_OPTIONS } from 'src/models/delivery-type/DeliveryType'
import { STAGES } from 'src/models/kitchen-display/KitchenDisplayType'
import { msPerMinute } from 'src/models/model'
import { ORDER_ITEM_STATES } from 'src/models/order-item/OrderItem'
import KitchenCardMixin from 'src/modules/kds/components/kitchen-card/mixins/KitchenCardMixin'

export default {
  name: 'KitchenOrderSlideItem',
  mixins: [KitchenCardMixin],
  configuration: ['currentSite', 'locations'],
  props: {
    deliveryType: {
      type: String,
      required: true
    }
  },
  emits: ['refresh'],
  computed: {
    actionableOrderItems () {
      return _.filter(this.orderItems, orderItem => {
        if (orderItem.state === ORDER_ITEM_STATES.VOIDED) return false

        if (_.includes([STAGES.OPEN, STAGES.COMPLETE], this.tab)) return !orderItem.isHeld

        if (this.tab === STAGES.PREPARE) return orderItem.isUnprepared && !orderItem.isHeld

        if (this.tab === STAGES.DELIVER) return orderItem.isReadyForDelivery && !orderItem.isHeld
      })
    },
    printAction () {
      const enabled = _.some(this.actionableOrderItems, orderItem => !orderItem.pickPrepPrintedAt && !orderItem.isVoided)
      const icon = enabled ? 'print' : 'print_disabled'
      return {
        class: 'col-12',
        label: 'print',
        icon,
        iconRight: icon,
        color: 'info',
        enabled,
        handler: () => this.printOrder()
      }
    },
    orderDisplayName () {
      let orderDisplayName

      if (this.kitchenDisplay.kitchenDisplayType.showOrderName) {
        orderDisplayName = this.order.nameForOrder || this.order.userOrDeliveryName
      }
      orderDisplayName ||= `#${this.order.id}`

      return orderDisplayName
    },
    orderOriginLabel () {
      const orderOrigin = this.kitchenDisplay.kitchenDisplayType.showOrderOrigin
        ? this.order.registerStation?.name ? `(${(this.order.registerStation?.name)})` : 'Remote'
        : null

      const orderEmployee = this.kitchenDisplay.kitchenDisplayType.showEmployee
        ? this.order.employee?.name
        : null

      const orderOriginLabel = _.compact([orderEmployee, orderOrigin]).join(' ')

      return orderOriginLabel !== '' ? orderOriginLabel : null
    },
    deliveredTime () {
      const deliveredTime = _.findLast(this.orderItems, 'isDelivered')?.siteDeliveredTime

      if (!deliveredTime) return null

      return `Delivered ${this.$d(deliveredTime, 'shortTime')}`
    },
    dueTime () {
      const dueTime = this.tab === 'open'
        ? _.first(this.orderItems)?.siteDueTime
        : _.last(this.orderItems)?.siteDueTime

      if (!dueTime) return 'On Hold'

      return `Due ${this.$d(dueTime, 'shortTime')}`
    },
    relativeTime () {
      const time = _.first(this.orderItems)?.siteDueTime

      if (!time) return null

      const offsetTime = this.currentSite.siteTimeWithSeconds
      return time.relativeTime({ relativeTo: offsetTime, compact: true, msPrecisionLimit: 10 * msPerMinute })
    },
    orderDeliveryTypeLabel () {
      if (!this.kitchenDisplay.kitchenDisplayType.showDeliveryLocations) return null

      const orderItem = _.first(this.orderItems)

      if (orderItem.packingRequired) {
        return 'TO PACKING'
      } else if (orderItem.pagerNumber) {
        return `Pager #${orderItem.pagerNumber}`
      } else {
        return _.startCase(orderItem.deliveryType)
      }
    },
    orderDeliveryLocationsLabel () {
      if (!this.kitchenDisplay.kitchenDisplayType.showDeliveryLocations) return null

      const orderItem = _.first(this.orderItems)

      if (orderItem.packingRequired) {
        return null
      } else if (orderItem.pagerNumber) {
        return null
      } else if (orderItem.deliveryType === DELIVERY_TYPE_OPTIONS.TABLE) {
        return `${orderItem.deliveryLocation || orderItem.defaultDeliveryLocation || 'NO TABLE'}`
      } else if (orderItem.deliveryType === DELIVERY_TYPE_OPTIONS.THEATER_SEAT) {
        const deliveryScreen = `${orderItem.deliveryLocation || orderItem.defaultDeliveryLocation || 'NO SCREEN'}`
        const allSeats = _(this.orderItems).map(orderItem => orderItem.deliveryPositions || orderItem.defaultDeliveryPositions).flatten().uniq().value().join(', ')
        const seats = ` ${allSeats || 'NO SEAT'}`
        return `${deliveryScreen}: ${seats}`
      } else if (orderItem.deliveryType === DELIVERY_TYPE_OPTIONS.REGISTER) {
        return `${orderItem.order.registerStation?.name}`
      } else {
        return null
      }
    }
  },
  created () {
    _.extend(this, { STAGES })
  },
  methods: {
    printOrder () {
      if (isElectronMode) {
        this.$printPickPrep(this.order.id, () => this.$emit('refresh', this.order.id))
      } else {
        const itemSourceLocationIds = _(this.actionableOrderItems).filter(orderItem => !orderItem.pickPrepPrintedAt && !orderItem.isVoided).map('itemSourceLocationId').uniq().value()
        _(this.locations.data).filter(location => _.includes(itemSourceLocationIds, location.id)).each(location => {
          location.printPickPrep(this.order.id)
        })
      }
    }
  }
}
</script>
